import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import { MEDIA_VARIANTS } from '../../../media';
import SearchPickerDialog from './SearchPickerDialog';

function noImageUrl() {
  return null;
}

function optionValue(option) {
  return option?.key
    ?? option?.Key
    ?? option?.id
    ?? option?.Id
    ?? '';
}

function optionLabel(option) {
  return option?.label
    ?? option?.Label
    ?? option?.name
    ?? option?.Name
    ?? optionValue(option);
}

function itemValue(item) {
  return item?.itemId ?? item?.ItemId ?? item?.id ?? item?.Id ?? '';
}

function itemLabel(item) {
  return item?.name ?? item?.Name ?? itemValue(item);
}

function itemTypeName(item) {
  return item?.itemTypeName ?? item?.ItemTypeName ?? '';
}

function itemMapImageId(item) {
  return item?.mapImageId ?? item?.MapImageId ?? '';
}

function itemSearchTerms(item) {
  const terms = item?.searchTerms ?? item?.SearchTerms ?? [];
  return Array.isArray(terms) ? terms : [];
}

function FieldName({ children, help }) {
  return (
    <span
      className="world-editor-field-name"
      title={help}
    >
      {children}
      <sup aria-hidden="true">?</sup>
    </span>
  );
}

function pickerOptions(options, group) {
  return options
    .map(option => {
      const value = optionValue(option);
      const label = optionLabel(option);
      if (!value || value === 'none') return null;
      return {
        value,
        label,
        group,
        keywords: [value],
        meta: value === label ? '' : value,
      };
    })
    .filter(Boolean);
}

export function SelectionPickerField({
  addLabel,
  description,
  label,
  onChange,
  optionalLabel,
  options,
  title,
  toggleId,
  values,
}) {
  const [open, setOpen] = useState(false);
  const returnFocusRef = useRef(null);
  const currentValues = Array.isArray(values) ? values : [];
  const enabled = !optionalLabel || currentValues.length > 0 || open;
  const optionsByValue = new Map(options.map(option => [option.value, option]));

  return (
    <>
      {optionalLabel ? (
        <label className="world-editor-checkbox" htmlFor={toggleId}>
          <input
            checked={enabled}
            id={toggleId}
            type="checkbox"
            onChange={(event) => {
              returnFocusRef.current = event.currentTarget;
              if (event.target.checked) setOpen(true);
              else onChange([]);
            }}
          />
          <span>{optionalLabel}</span>
        </label>
      ) : null}
      {enabled ? (
        <fieldset className="world-editor-selection-field">
          <legend>{label}</legend>
          {description ? <p>{description}</p> : null}
          <div className="world-editor-selection-field__values">
            {currentValues.length === 0 ? (
              <span className="world-editor-selection-field__empty">
                No restriction
              </span>
            ) : currentValues.map(value => (
              <span className="world-editor-selection-chip" key={value}>
                {optionsByValue.get(value)?.label ?? value}
                <button
                  aria-label={`Remove ${optionsByValue.get(value)?.label ?? value}`}
                  type="button"
                  onClick={() => onChange(
                    currentValues.filter(current => current !== value),
                  )}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </span>
            ))}
            <button
              aria-haspopup="dialog"
              className="button-secondary world-editor-selection-field__add"
              type="button"
              onClick={(event) => {
                returnFocusRef.current = event.currentTarget;
                setOpen(true);
              }}
            >
              + {addLabel}
            </button>
          </div>
          {open ? (
            <SearchPickerDialog
              description={description}
              multiple
              onApply={onChange}
              onClose={() => setOpen(false)}
              options={options}
              returnFocusRef={returnFocusRef}
              searchLabel="Search tiles by name or key"
              selectedValues={currentValues}
              title={title}
            />
          ) : null}
        </fieldset>
      ) : null}
    </>
  );
}

function ItemPickerField({
  getImageUrl,
  itemId,
  onChange,
  options,
  ruleName,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const selected = options.find(option => option.value === itemId);
  const selectedImageUrl = selected?.imageId
    ? getImageUrl(selected.imageId, MEDIA_VARIANTS.ENTITY_ICON)
    : null;
  return (
    <fieldset className="world-editor-selection-field">
      <legend>
        <FieldName help="The item and map artwork shown for every occurrence created by this rule.">
          Resource
        </FieldName>
      </legend>
      <button
        aria-haspopup="dialog"
        className="world-editor-item-picker"
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
      >
        <span className="world-editor-item-picker__selection">
          {selectedImageUrl ? (
            <span
              aria-hidden="true"
              className="world-editor-item-picker__image"
              style={{ backgroundImage: `url(${selectedImageUrl})` }}
            />
          ) : null}
          <span>
            <strong>{selected?.label ?? 'Choose an item…'}</strong>
            {selected?.meta ? <small>{selected.meta}</small> : null}
          </span>
        </span>
        <span aria-hidden="true">⌕</span>
      </button>
      {open ? (
        <SearchPickerDialog
          description="Fuzzy search works with partial names, item types, stats, and consumable effects."
          onApply={values => onChange(values[0] || '')}
          onClose={() => setOpen(false)}
          options={options}
          returnFocusRef={triggerRef}
          selectedValues={itemId ? [itemId] : []}
          title={`Choose spawned item for ${ruleName || 'resource rule'}`}
        />
      ) : null}
    </fieldset>
  );
}

function PreviewSummary({
  getImageUrl,
  itemOptions,
  preview,
  rules,
}) {
  if (!preview) return null;
  const errors = preview.errors ?? preview.Errors ?? [];
  const placements = preview.effectivePlacements
    ?? preview.EffectivePlacements
    ?? [];
  const ruleResults = preview.rules ?? preview.Rules ?? [];
  const valid = preview.isValid ?? preview.IsValid ?? errors.length === 0;
  const configuredRules = new Map(rules.map(rule => [rule.key, rule]));
  const items = new Map(itemOptions.map(item => [item.value, item]));

  return (
    <div
      className={`resource-preview-summary resource-preview-summary--${valid ? 'valid' : 'invalid'}`}
      role={valid ? 'status' : 'alert'}
    >
      <strong>
        {valid
          ? `${placements.length} effective resource nodes`
          : `${errors.length} validation issue${errors.length === 1 ? '' : 's'}`}
      </strong>
      {errors.length > 0 && (
        <ul>
          {errors.map((error, index) => (
            <li key={`${error.path ?? error.Path ?? 'error'}-${index}`}>
              {error.message ?? error.Message ?? 'Invalid resource rule.'}
            </li>
          ))}
        </ul>
      )}
      {valid && ruleResults.length > 0 ? (
        <ul className="resource-preview-summary__rules">
          {ruleResults.map((result) => {
            const ruleKey = result.ruleKey ?? result.RuleKey ?? '';
            const configuredRule = configuredRules.get(ruleKey);
            const item = items.get(configuredRule?.itemId);
            const imageUrl = item?.imageId
              ? getImageUrl(item.imageId, MEDIA_VARIANTS.ENTITY_ICON)
              : null;
            const selectedCount = result.selectedCount
              ?? result.SelectedCount
              ?? 0;
            const eligibleCount = result.eligibleCount
              ?? result.EligibleCount
              ?? 0;
            return (
              <li key={ruleKey}>
                {imageUrl ? (
                  <span
                    aria-hidden="true"
                    className="resource-preview-summary__image"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                ) : null}
                <span>
                  <strong>{item?.label ?? ruleKey}</strong>
                  <small>
                    {selectedCount} occurrence{selectedCount === 1 ? '' : 's'}
                    {' '}on {eligibleCount} eligible tile{eligibleCount === 1 ? '' : 's'}
                  </small>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function ResourceGatheringFields({ onChange, prefix, value }) {
  return (
    <>
      <label className="form-control" htmlFor={`${prefix}-stack-min`}>
        <FieldName help="The smallest number of items awarded by one completed scavenge.">
          Yield minimum
        </FieldName>
        <input
          id={`${prefix}-stack-min`}
          max="20"
          min="1"
          title="The smallest number of items awarded by one completed scavenge."
          type="number"
          value={value.minimumStack}
          onChange={(event) => onChange({
            minimumStack: Math.min(
              20,
              Math.max(1, Number(event.target.value) || 1),
            ),
          })}
        />
      </label>
      <label className="form-control" htmlFor={`${prefix}-stack-max`}>
        <FieldName help="The largest number of items awarded by one completed scavenge.">
          Yield maximum
        </FieldName>
        <input
          id={`${prefix}-stack-max`}
          max="20"
          min="1"
          title="The largest number of items awarded by one completed scavenge."
          type="number"
          value={value.maximumStack}
          onChange={(event) => onChange({
            maximumStack: Math.min(
              20,
              Math.max(1, Number(event.target.value) || 1),
            ),
          })}
        />
      </label>
      <label className="form-control" htmlFor={`${prefix}-charges`}>
        <FieldName help="How many times each occurrence can be scavenged before it is depleted.">
          Stack size
        </FieldName>
        <input
          id={`${prefix}-charges`}
          min="1"
          title="How many times each occurrence can be scavenged before it is depleted."
          type="number"
          value={value.maximumCharges}
          onChange={(event) => onChange({
            maximumCharges: Math.max(
              1,
              Number(event.target.value) || 1,
            ),
          })}
        />
      </label>
      <label className="form-control" htmlFor={`${prefix}-respawn`}>
        <FieldName help="How many seconds a depleted occurrence stays unavailable before it returns.">
          Respawn delay (seconds)
        </FieldName>
        <input
          id={`${prefix}-respawn`}
          min="1"
          title="How many seconds a depleted occurrence stays unavailable before it returns."
          type="number"
          value={value.respawnSeconds}
          onChange={(event) => onChange({
            respawnSeconds: Math.max(
              1,
              Number(event.target.value) || 1,
            ),
          })}
        />
      </label>
    </>
  );
}

function ResourceSpawnRulesEditor({
  baseTiles,
  detailTiles,
  onAdd,
  onAddPlacement,
  onChange,
  onChangePlacement,
  onPlace,
  onPreview,
  onRemove,
  onRemovePlacement,
  placementMode = '',
  placements = [],
  preview,
  previewBusy,
  previewError,
  rules,
  spawnableItems = [],
  spawnableItemsError,
  getImageUrl = noImageUrl,
}) {
  const itemOptions = useMemo(() => spawnableItems.map(item => {
    const typeName = itemTypeName(item);
    const terms = itemSearchTerms(item);
    const imageId = itemMapImageId(item);
    const details = [
      typeName,
      ...terms.filter(term => term !== typeName).slice(0, 4),
    ].filter(Boolean);
    return {
      value: itemValue(item),
      label: itemLabel(item),
      group: typeName,
      imageId,
      keywords: terms,
      meta: details.join(' · '),
    };
  }).filter(option => option.value), [spawnableItems]);
  const baseOptions = useMemo(
    () => pickerOptions(baseTiles, 'Base tiles'),
    [baseTiles],
  );
  const detailOptions = useMemo(
    () => pickerOptions(detailTiles, 'Details'),
    [detailTiles],
  );

  return (
    <section className="world-editor-section" aria-labelledby="resource-rules-title">
      <div className="world-editor-section__heading">
        <div>
          <h3 id="resource-rules-title">Resources</h3>
          <p>
            Place fixed resource nodes or generate rotating occurrences from
            habitat rules. Every scavenge grants the configured random yield.
          </p>
        </div>
      </div>

      <div className="world-editor-section__heading">
        <h4>Fixed resource nodes</h4>
        <button
          className="button-secondary"
          disabled={spawnableItems.length === 0}
          type="button"
          onClick={onAddPlacement}
        >
          Add Resource
        </button>
      </div>

      {spawnableItemsError && (
        <p className="world-editor-feedback world-editor-feedback--error" role="alert">
          {spawnableItemsError}
        </p>
      )}

      {placements.length === 0 && (
        <p className="world-editor-empty">No fixed resource nodes.</p>
      )}

      <div className="world-editor-card-list">
        {placements.map((placement, index) => {
          const prefix = `resource-placement-${index}`;
          const item = itemOptions.find(option => (
            option.value === placement.itemId
          ));
          return (
            <details
              className="world-editor-card"
              key={`resource-placement-${index}`}
            >
              <summary>
                <span>{placement.placementId || item?.label || `Resource ${index + 1}`}</span>
                <span className={placement.playerScoped ? 'status-enabled' : 'status-disabled'}>
                  {placement.playerScoped ? 'Player-specific' : 'Shared'}
                </span>
              </summary>
              <div className="world-editor-card__body">
                <label className="form-control" htmlFor={`${prefix}-id`}>
                  <FieldName help="A unique, stable identifier for this exact map placement.">
                    Placement ID
                  </FieldName>
                  <input
                    id={`${prefix}-id`}
                    value={placement.placementId}
                    onChange={event => onChangePlacement(index, {
                      placementId: event.target.value,
                    })}
                  />
                </label>
                <ItemPickerField
                  getImageUrl={getImageUrl}
                  itemId={placement.itemId}
                  onChange={itemId => onChangePlacement(index, { itemId })}
                  options={itemOptions}
                  ruleName={placement.placementId || `Resource ${index + 1}`}
                />
                <div className="world-editor-number-grid">
                  <ResourceGatheringFields
                    onChange={patch => onChangePlacement(index, patch)}
                    prefix={prefix}
                    value={placement}
                  />
                </div>
                <label className="form-control" htmlFor={`${prefix}-unlock`}>
                  <FieldName help="Optional run unlock required before this resource becomes visible.">
                    Required run unlock
                  </FieldName>
                  <input
                    id={`${prefix}-unlock`}
                    placeholder="No unlock required"
                    value={placement.requiredRunUnlockKey}
                    onChange={event => onChangePlacement(index, {
                      requiredRunUnlockKey: event.target.value,
                    })}
                  />
                </label>
                <label className="world-editor-checkbox" htmlFor={`${prefix}-player-scoped`}>
                  <input
                    checked={placement.playerScoped}
                    id={`${prefix}-player-scoped`}
                    type="checkbox"
                    onChange={event => onChangePlacement(index, {
                      playerScoped: event.target.checked,
                    })}
                  />
                  <span>Keep charges and respawn state per player</span>
                </label>
                <p>
                  Position: {placement.x}, {placement.y}
                </p>
                <div className="world-editor-card__actions">
                  <button
                    className={`button-secondary ${
                      placementMode === `resource:${placement.placementId}`
                        ? 'is-active'
                        : ''
                    }`}
                    type="button"
                    onClick={() => onPlace(placement.placementId)}
                  >
                    {placementMode === `resource:${placement.placementId}`
                      ? 'Click a tile…'
                      : 'Place on Map'}
                  </button>
                  <button
                    className="button-critical"
                    type="button"
                    onClick={() => onRemovePlacement(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </details>
          );
        })}
      </div>

      <div className="world-editor-section__heading">
        <div>
          <h4>Habitat rules</h4>
          <p>
            Preview to see the exact generated artwork and locations on the map.
          </p>
        </div>
        <button
          className="button-secondary"
          disabled={spawnableItems.length === 0}
          type="button"
          onClick={onAdd}
        >
          Add Rule
        </button>
      </div>

      {rules.length === 0 && (
        <p className="world-editor-empty">
          No habitat rules.
        </p>
      )}

      <div className="world-editor-card-list">
        {rules.map((rule, index) => {
          const prefix = `resource-rule-${index}`;
          const near = rule.near || {
            baseKeys: [],
            detailKeysAny: [],
            maxManhattanDistance: 1,
          };
          const updateNear = patch => onChange(index, {
            near: { ...near, ...patch },
          });
          return (
            <details className="world-editor-card" key={`resource-rule-${index}`}>
              <summary>
                <span>{rule.key || `Rule ${index + 1}`}</span>
                <span className={rule.enabled ? 'status-enabled' : 'status-disabled'}>
                  {rule.enabled
                    ? `${rule.count} occurrence${rule.count === 1 ? '' : 's'}`
                    : 'Disabled'}
                </span>
              </summary>
              <div className="world-editor-card__body">
                <label className="form-control" htmlFor={`${prefix}-key`}>
                  <FieldName help="A unique, stable identifier. Changing it changes this rule's deterministic placement sequence.">
                    Rule key
                  </FieldName>
                  <input
                    id={`${prefix}-key`}
                    title="A unique, stable identifier. Changing it changes this rule's deterministic placement sequence."
                    value={rule.key}
                    onChange={(event) => onChange(index, { key: event.target.value })}
                  />
                </label>
                <ItemPickerField
                  getImageUrl={getImageUrl}
                  itemId={rule.itemId}
                  onChange={itemId => onChange(index, { itemId })}
                  options={itemOptions}
                  ruleName={rule.key || `Rule ${index + 1}`}
                />
                <div className="world-editor-number-grid">
                  <label className="form-control" htmlFor={`${prefix}-count`}>
                    <FieldName help="How many occurrences of this resource should be active on the map at the same time.">
                      Occurrences
                    </FieldName>
                    <input
                      id={`${prefix}-count`}
                      min="1"
                      title="How many occurrences of this resource should be active on the map at the same time."
                      type="number"
                      value={rule.count}
                      onChange={(event) => onChange(index, {
                        count: Math.max(1, Number(event.target.value) || 1),
                      })}
                    />
                  </label>
                  <ResourceGatheringFields
                    onChange={patch => onChange(index, patch)}
                    prefix={prefix}
                    value={rule}
                  />
                  <label className="form-control" htmlFor={`${prefix}-spacing`}>
                    <FieldName help="Minimum Manhattan-tile distance between resource occurrences.">
                      Minimum spacing (tiles)
                    </FieldName>
                    <input
                      id={`${prefix}-spacing`}
                      min="0"
                      title="Minimum Manhattan-tile distance between resource occurrences."
                      type="number"
                      value={rule.minimumSpacing}
                      onChange={(event) => onChange(index, {
                        minimumSpacing: Math.max(
                          0,
                          Number(event.target.value) || 0,
                        ),
                      })}
                    />
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-spawn-radius`}>
                    <FieldName help="Keeps resources this many Manhattan tiles away from the player spawn, Camp entrance, and region exits.">
                      Entrance clearance (tiles)
                    </FieldName>
                    <input
                      id={`${prefix}-spawn-radius`}
                      min="0"
                      title="Keeps resources this many Manhattan tiles away from the player spawn, Camp entrance, and region exits."
                      type="number"
                      value={rule.excludeSpawnRadius}
                      onChange={(event) => onChange(index, {
                        excludeSpawnRadius: Math.max(
                          0,
                          Number(event.target.value) || 0,
                        ),
                      })}
                    />
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-rotation-cycle`}>
                    <FieldName help="How long an occurrence remains at one location. One occurrence is replaced every cycle divided by the occurrence count.">
                      Rotation cycle (hours)
                    </FieldName>
                    <input
                      id={`${prefix}-rotation-cycle`}
                      min="0.01"
                      step="0.01"
                      title="How long an occurrence remains at one location. One occurrence is replaced every cycle divided by the occurrence count."
                      type="number"
                      value={rule.rotationCycleSeconds / 3600}
                      onChange={(event) => onChange(index, {
                        rotationCycleSeconds: Math.max(
                          1,
                          Math.round(
                            (Number(event.target.value) || 24) * 3600,
                          ),
                        ),
                      })}
                    />
                  </label>
                </div>
                <SelectionPickerField
                  addLabel="Add allowed base tiles"
                  description="The resource may spawn on any selected base tile."
                  label="Allowed base tiles (any)"
                  onChange={values => onChange(index, { allowedBaseKeys: values })}
                  options={baseOptions}
                  title={`Allowed base tiles for ${rule.key || `Rule ${index + 1}`}`}
                  values={rule.allowedBaseKeys}
                />
                <SelectionPickerField
                  addLabel="Add required details"
                  description="At least one selected detail must be on the spawn tile."
                  label="Required details (any)"
                  onChange={values => onChange(index, {
                    allowedDetailKeysAny: values,
                  })}
                  optionalLabel="Require details on the spawn tile"
                  options={detailOptions}
                  title={`Required details for ${rule.key || `Rule ${index + 1}`}`}
                  toggleId={`${prefix}-details-enabled`}
                  values={rule.allowedDetailKeysAny}
                />
                <label className="world-editor-checkbox" htmlFor={`${prefix}-near-enabled`}>
                  <input
                    checked={Boolean(rule.near)}
                    id={`${prefix}-near-enabled`}
                    type="checkbox"
                    onChange={(event) => onChange(index, {
                      near: event.target.checked ? near : null,
                    })}
                  />
                  <span>Require nearby terrain or details</span>
                </label>
                {rule.near ? (
                  <>
                    <SelectionPickerField
                      addLabel="Add nearby base tiles"
                      description="At least one selected base tile must be within the nearby distance."
                      label="Must be near base tiles (any)"
                      onChange={values => updateNear({ baseKeys: values })}
                      options={baseOptions}
                      title={`Nearby base tiles for ${rule.key || `Rule ${index + 1}`}`}
                      values={near.baseKeys}
                    />
                    <SelectionPickerField
                      addLabel="Add nearby details"
                      description="At least one selected detail must be within the nearby distance."
                      label="Must be near details (any)"
                      onChange={values => updateNear({ detailKeysAny: values })}
                      options={detailOptions}
                      title={`Nearby details for ${rule.key || `Rule ${index + 1}`}`}
                      values={near.detailKeysAny}
                    />
                    <label className="form-control" htmlFor={`${prefix}-near-distance`}>
                      <FieldName help="Maximum Manhattan-tile distance to one of the selected nearby terrain or detail tiles.">
                        Nearby distance (tiles)
                      </FieldName>
                      <input
                        id={`${prefix}-near-distance`}
                        min="1"
                        title="Maximum Manhattan-tile distance to one of the selected nearby terrain or detail tiles."
                        type="number"
                        value={near.maxManhattanDistance}
                        onChange={(event) => updateNear({
                          maxManhattanDistance: Math.max(
                            1,
                            Number(event.target.value) || 1,
                          ),
                        })}
                      />
                    </label>
                  </>
                ) : null}
                <label className="world-editor-checkbox" htmlFor={`${prefix}-enabled`}>
                  <input
                    checked={rule.enabled}
                    id={`${prefix}-enabled`}
                    type="checkbox"
                    onChange={(event) => onChange(index, {
                      enabled: event.target.checked,
                    })}
                  />
                  <span>Use this rule</span>
                </label>
                <div className="world-editor-card__actions">
                  <button
                    className="button-critical"
                    type="button"
                    onClick={() => onRemove(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </details>
          );
        })}
      </div>

      <button
        className="button-secondary"
        disabled={previewBusy}
        type="button"
        onClick={onPreview}
      >
        {previewBusy
          ? 'Calculating Preview…'
          : preview ? 'Update Map Preview' : 'Preview Resources on Map'}
      </button>
      {previewError && (
        <p className="world-editor-feedback world-editor-feedback--error" role="alert">
          {previewError}
        </p>
      )}
      <PreviewSummary
        getImageUrl={getImageUrl}
        itemOptions={itemOptions}
        preview={preview}
        rules={rules}
      />
    </section>
  );
}

export default ResourceSpawnRulesEditor;
